import { useState, useEffect } from "react";
import {
  useAssetEstimate,
  EstimateResult,
  RealEstateEstimateData,
  VehicleEstimateData,
} from "@/shared/hooks/useAssetEstimate";
import { RealEstateEstimateForm } from "./RealEstateEstimateForm";
import { VehicleEstimateForm } from "./VehicleEstimateForm";
import { EstimateLoadingAnimation } from "./EstimateLoadingAnimation";
import { EstimateResultCard } from "./EstimateResultCard";

interface AIAssetEstimateFormProps {
  estateId: number;
  assetType: "real-estate" | "vehicle";
  onConfirm: (data: { itemName: string; itemType?: string; value: number }) => void;
  onCancel: () => void;
}

export function AIAssetEstimateForm({
  estateId,
  assetType,
  onConfirm,
  onCancel,
}: AIAssetEstimateFormProps) {
  const [result, setResult] = useState<EstimateResult | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  const estimateMutation = useAssetEstimate();

  // Ensure minimum 2.5 second loading animation
  useEffect(() => {
    if (estimateMutation.isPending) {
      setShowLoading(true);
    } else if (estimateMutation.isSuccess && showLoading) {
      const timer = setTimeout(() => {
        setShowLoading(false);
        setResult(estimateMutation.data);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [estimateMutation.isPending, estimateMutation.isSuccess, estimateMutation.data, showLoading]);

  const handleRealEstateEstimate = (address: string) => {
    estimateMutation.mutate({
      estateId,
      assetType: "real-estate",
      data: { address } as RealEstateEstimateData,
    });
  };

  const handleVehicleEstimate = (data: VehicleEstimateData) => {
    estimateMutation.mutate({
      estateId,
      assetType: "vehicle",
      data,
    });
  };

  const handleConfirm = (value: number, itemType?: string) => {
    if (result) {
      onConfirm({
        itemName: result.suggestedItemName,
        itemType: itemType || result.suggestedItemType,
        value,
      });
    }
  };

  const handleRetry = () => {
    estimateMutation.reset();
    setResult(null);
    setShowLoading(false);
  };

  if (showLoading || estimateMutation.isPending) {
    return <EstimateLoadingAnimation assetType={assetType} />;
  }

  if (estimateMutation.isError) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">
            Failed to estimate asset value. Please try again or enter manually.
          </p>
          {estimateMutation.error && (
            <p className="text-sm text-red-600 mt-2">{estimateMutation.error.message}</p>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleRetry}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <EstimateResultCard
        result={result}
        assetType={assetType}
        onConfirm={handleConfirm}
        onCancel={onCancel}
      />
    );
  }

  return (
    <div>
      {assetType === "real-estate" ? (
        <RealEstateEstimateForm
          onEstimate={handleRealEstateEstimate}
          isLoading={estimateMutation.isPending}
        />
      ) : (
        <VehicleEstimateForm
          onEstimate={handleVehicleEstimate}
          isLoading={estimateMutation.isPending}
        />
      )}
    </div>
  );
}
